import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { api, formatApiError } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { toast } from "sonner";

const COLORS = ["#1A3556", "#3F5F88", "#6E94B6", "#B49872", "#8E6F5B", "#547888", "#A0512E", "#3D6F6F"];

function buildInitialForm() {
  return {
    title: "",
    chart_type: "line",
    sql_query: "SELECT day AS x, revenue AS y FROM daily_metrics ORDER BY day",
    datasource_id: "",
    x_field: "x",
    y_field: "y",
    x_label: "",
    y_label: "",
    color: "#1A3556",
    refresh_seconds: 60,
    width: 6,
    height: 280,
  };
}

export default function ChartFormDialog({ open, onOpenChange, dashboardId, chart, onSaved }) {
  const editing = Boolean(chart);
  const t = useT();
  const CHART_TYPES = [
    { value: "line", label: t("chart.line") },
    { value: "area", label: t("chart.area") },
    { value: "bar", label: t("chart.bar") },
    { value: "scatter", label: t("chart.scatter") },
  ];
  const [datasources, setDatasources] = useState([]);
  const [form, setForm] = useState(buildInitialForm());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api
      .get("/datasources")
      .then(({ data }) => setDatasources(data || []))
      .catch((err) => {
        console.error("Failed to load datasources for chart form:", err);
        setDatasources([]);
      });
    setForm(
      chart
        ? {
            title: chart.title,
            chart_type: chart.chart_type,
            sql_query: chart.sql_query,
            datasource_id: chart.datasource_id,
            x_field: chart.x_field || "x",
            y_field: chart.y_field || "y",
            x_label: chart.x_label || "",
            y_label: chart.y_label || "",
            color: chart.color || "#1A3556",
            refresh_seconds: chart.refresh_seconds || 60,
            width: chart.width || 6,
            height: chart.height || 280,
          }
        : buildInitialForm()
    );
  }, [open, chart]);

  function update(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function submit() {
    if (!form.title || !form.sql_query || !form.datasource_id) {
      toast.error(t("chart.required"));
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/charts/${chart.id}`, form);
        toast.success(t("chart.updated"));
      } else {
        await api.post("/charts", { ...form, dashboard_id: dashboardId });
        toast.success(t("chart.created"));
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="chart-form-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? t("chart.edit") : t("chart.new")}</DialogTitle>
          <DialogDescription>
            {t("chart.sql_query")} → <code className="text-xs">x</code>, <code className="text-xs">y</code>.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2 sm:col-span-1 space-y-1.5">
            <Label htmlFor="cf-title">{t("chart.title")}</Label>
            <Input
              id="cf-title"
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              placeholder="Daily Revenue"
              data-testid="chart-form-title"
            />
          </div>
          <div className="col-span-2 sm:col-span-1 space-y-1.5">
            <Label>{t("chart.type")}</Label>
            <Select value={form.chart_type} onValueChange={(v) => update("chart_type", v)}>
              <SelectTrigger data-testid="chart-form-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CHART_TYPES.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label>{t("chart.datasource")}</Label>
            <Select value={form.datasource_id} onValueChange={(v) => update("datasource_id", v)}>
              <SelectTrigger data-testid="chart-form-datasource"><SelectValue placeholder={t("chart.choose")} /></SelectTrigger>
              <SelectContent>
                {datasources.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    <span className="font-mono text-[11px] mr-1 uppercase">{d.kind}</span> {d.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label htmlFor="cf-sql">{t("chart.sql_query")}</Label>
            <Textarea
              id="cf-sql"
              value={form.sql_query}
              onChange={(e) => update("sql_query", e.target.value)}
              rows={5}
              className="font-mono text-xs"
              data-testid="chart-form-sql"
            />
            <p className="text-[11px] text-muted-foreground">
              Tip: usa <code className="font-mono">:from</code> y <code className="font-mono">:to</code> en el WHERE para
              filtrar por el rango de tiempo seleccionado en el dashboard. Ej:{" "}
              <code className="font-mono">WHERE ts BETWEEN :from AND :to</code>. Si no los incluyes, la consulta corre tal cual.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cf-x">{t("chart.x_field")}</Label>
            <Input id="cf-x" value={form.x_field} onChange={(e) => update("x_field", e.target.value)} data-testid="chart-form-xfield" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cf-y">{t("chart.y_field")}</Label>
            <Input id="cf-y" value={form.y_field} onChange={(e) => update("y_field", e.target.value)} data-testid="chart-form-yfield" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cf-xl">{t("chart.x_label")}</Label>
            <Input id="cf-xl" value={form.x_label} onChange={(e) => update("x_label", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cf-yl">{t("chart.y_label")}</Label>
            <Input id="cf-yl" value={form.y_label} onChange={(e) => update("y_label", e.target.value)} />
          </div>
          <div className="space-y-1.5 col-span-2 sm:col-span-1">
            <Label>{t("chart.color")}</Label>
            <div className="flex flex-wrap gap-1.5">
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => update("color", c)}
                  className={`h-7 w-7 rounded-md border-2 transition-transform ${
                    form.color === c ? "scale-110 border-foreground" : "border-transparent"
                  }`}
                  style={{ backgroundColor: c }}
                  data-testid={`chart-form-color-${c.replace("#", "")}`}
                  aria-label={c}
                />
              ))}
            </div>
          </div>
          <div className="space-y-1.5 col-span-2 sm:col-span-1">
            <Label htmlFor="cf-refresh">{t("chart.refresh_seconds")}</Label>
            <Input
              id="cf-refresh"
              type="number"
              min={15}
              value={form.refresh_seconds}
              onChange={(e) => update("refresh_seconds", parseInt(e.target.value) || 60)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="chart-form-cancel">
            {t("common.cancel")}
          </Button>
          <Button onClick={submit} disabled={saving} data-testid="chart-form-save">
            {saving ? t("common.loading") : editing ? t("common.saveChanges") : t("chart.create_chart")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
